import { Dispatch } from 'redux';
import BotAnalyticsController from '../controllers/BotAnalyticsController';
import commonHandler from '../errorHandlers/common-handler';
import {
  AnalyticsParams,
  DemographicAnalyticsCategory,
  XRayAnalyticsType,
} from '../interfaces/botAnalytics';
import { MyLeadsAttributes } from '../interfaces/myLeads';
import { StoreState } from '../store-interfaces';
import { getActionHookBuilder } from './action-hook-builder';
import CrudActionContainer from './CrudActionContainer';

class BotAnalyticsCRUDActions extends CrudActionContainer {
  retrieveTopDialoguesAction: string;
  retrieveContactsAmountAnalyticsAction: string;
  retrieveUnansweredQuestionsAction: string;
  retrieveContactsBySourceAction: string;
  retrieveContactsByPlatformAction: string;
  retrieveQAKeywordsAction: string;
  retrieveContactsByPlatformAggregatedAction: string;
  retrieveEmailAnalyticsAction: string;
  retrieveXRayAnalyticsAction: string;
  retrieveContactsSourcesAnalyticsAction: string;
  retrieveDemographicAnalyticsAction: string;
  retrieveContactsGatheredAction: string;
  clearEmailAnalyticsAction: string;
  resetAllAction: string;

  constructor() {
    super('BOT_ANALYTICS');
    this.retrieveTopDialoguesAction = this.makeActionKey(
      'RETRIEVED_TOP_DIALOGUES',
    );
    this.retrieveContactsAmountAnalyticsAction = this.makeActionKey(
      'RETRIEVED_CONTACTS_AMOUNT_ANALYTICS',
    );
    this.retrieveUnansweredQuestionsAction = this.makeActionKey(
      'RETRIEVED_UNANSWERED_QUESTIONS_ANALYTICS',
    );
    this.retrieveContactsBySourceAction = this.makeActionKey(
      'RETRIEVED_CONTACTS_BY_SOURCE_ANALYTICS',
    );
    this.retrieveContactsByPlatformAction = this.makeActionKey(
      'RETRIEVED_CONTACTS_BY_PLATFORM_ANALYTICS',
    );
    this.retrieveQAKeywordsAction = this.makeActionKey(
      'RETRIEVED_QA_KEYWORDS_ANALYTICS',
    );
    this.retrieveContactsByPlatformAggregatedAction = this.makeActionKey(
      'RETRIEVED_CONTACTS_BY_PLATFORM_AGGREGATED_ANALYTICS',
    );
    this.retrieveEmailAnalyticsAction = this.makeActionKey(
      'RETRIEVED_EMAIL_ANALYTICS',
    );
    this.retrieveXRayAnalyticsAction = this.makeActionKey(
      'RETRIEVED_XRAY_ANALYTICS',
    );
    this.clearEmailAnalyticsAction = this.makeActionKey(
      'CLEAR_EMAIL_ANALYTICS',
    );
    this.retrieveContactsSourcesAnalyticsAction = this.makeActionKey(
      'RETRIEVED_CONTACTS_SOURCES_ANALYTICS',
    );
    this.retrieveDemographicAnalyticsAction = this.makeActionKey(
      'RETRIEVED_DEMOGRAPHIC_ANALYTICS',
    );
    this.retrieveContactsGatheredAction = this.makeActionKey(
      'RETRIEVED_CONTACTS_GATHERED_ANALYTICS',
    );
    this.resetAllAction = this.makeActionKey('REST_ALL_BOT_ANALYTICS');
  }

  retrieveTopDialogues(
    botId: number,
    pageNumber?: number,
    perPage?: number,
    startDate?: string,
    endDate?: string,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveTopDialoguesAction));
      try {
        const response = await BotAnalyticsController.getTopDialogues(
          botId,
          pageNumber,
          perPage,
          startDate,
          endDate,
        );
        dispatch({
          type: this.retrieveTopDialoguesAction,
          topDialogues: response,
        });

        return response;
      } catch (error) {
        commonHandler(dispatch)(error);
      }
    };
  }
  retrieveContactsAmountAnalytics(
    botId: number,
    startDate?: string,
    endDate?: string,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveContactsAmountAnalyticsAction));
      try {
        const response =
          await BotAnalyticsController.getContactsAmountAnalytics(
            botId,
            startDate,
            endDate,
          );

        dispatch({
          type: this.retrieveContactsAmountAnalyticsAction,
          contactsAmountAnalytics: response,
        });

        return response;
      } catch (error) {
        commonHandler(dispatch)(error);
      }
    };
  }
  retrieveUnansweredQuestions(
    botId: number,
    startDate?: string,
    endDate?: string,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveUnansweredQuestionsAction));
      try {
        const response = await BotAnalyticsController.getUnansweredQuestions(
          botId,
          startDate,
          endDate,
        );

        dispatch({
          type: this.retrieveUnansweredQuestionsAction,
          unansweredQuestions: response,
        });
        return response;
      } catch (error) {
        commonHandler(dispatch)(error);
      }
    };
  }
  exportUnansweredQuestions(
    botId: number,
    email: string,
    startDate?: string,
    endDate?: string,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const response = await BotAnalyticsController.exportUnansweredQuestions(
          botId,
          email,
          startDate,
          endDate,
        );
        return response;
      } catch (error) {
        commonHandler(dispatch)(error);
      }
    };
  }

  retrieveContactsByType(
    botId: number,
    type: 'by_source' | 'by_platform' | 'by_platform_aggregated',
    startDate?: string,
    endDate?: string,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveUnansweredQuestionsAction));
      try {
        const response = await BotAnalyticsController.getContactsByType(
          type,
          botId,
          startDate,
          endDate,
        );
        if (type === 'by_source') {
          dispatch({
            type: this.retrieveContactsBySourceAction,
            contactsBySource: response,
          });
        } else if (type === 'by_platform') {
          dispatch({
            type: this.retrieveContactsByPlatformAction,
            contactsByPlatform: response,
          });
        } else {
          dispatch({
            type: this.retrieveContactsByPlatformAggregatedAction,
            contactsByPlatformAggregated: response,
          });
        }

        return response;
      } catch (error) {
        commonHandler(dispatch)(error);
      }
    };
  }

  retrieveQAKeywords(botId: number, startDate?: string, endDate?: string) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveQAKeywordsAction));
      try {
        const response = await BotAnalyticsController.getQAKeywords(
          botId,
          startDate,
          endDate,
        );
        dispatch({
          type: this.retrieveQAKeywordsAction,
          qaKeywords: response.triggers,
        });

        return response;
      } catch (error) {
        commonHandler(dispatch)(error);
      }
    };
  }
  exportQAKeywords(
    botId: number,
    email: string,
    startDate?: string,
    endDate?: string,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const response = await BotAnalyticsController.exportQAKeywords(
          botId,
          email,
          startDate,
          endDate,
        );
        return response;
      } catch (error) {
        commonHandler(dispatch)(error);
      }
    };
  }

  retrieveEmailAnalytics(
    ...args: Parameters<typeof BotAnalyticsController.getEmailAnalytics>
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveEmailAnalyticsAction));
      try {
        const { tab } = args[0];
        const response = await BotAnalyticsController.getEmailAnalytics(
          ...args,
        );

        dispatch({
          type: this.retrieveEmailAnalyticsAction,
          emailAnalyticsByTab: {
            tab,
            payload: response,
          },
        });
        return response;
      } catch (e) {
        console.error(e);
        throw e;
      }
    };
  }

  retrieveXRayAnalytics(type: XRayAnalyticsType, params: AnalyticsParams) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const response = await BotAnalyticsController.getXRayAnalytics(
          type,
          params,
        );

        dispatch({
          type: this.retrieveXRayAnalyticsAction,
          xRayAnalytics: {
            type,
            payload: response,
          },
        });
        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  retrieveDemographicAnalytics(
    botId: number,
    category: DemographicAnalyticsCategory,
    query?: MyLeadsAttributes,
    audiencesIds?: number[],
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const response = await BotAnalyticsController.getDemographicAnalytics(
          botId,
          category,
          query,
          audiencesIds,
        );

        dispatch({
          type: this.retrieveDemographicAnalyticsAction,
          demographicAnalytics: {
            category,
            data: response,
          },
        });
        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  retrieveContactsSourcesAnalytics(botId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const response =
          await BotAnalyticsController.getContactsSourcesAnalytics(botId);

        dispatch({
          type: this.retrieveContactsSourcesAnalyticsAction,
          contactsSources: response,
        });
        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  retrieveContactsGathered(
    companyId: number,
    startDate?: string,
    endDate?: string,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveContactsGatheredAction));
      try {
        const response =
          await BotAnalyticsController.getContactsGatheredAnalytics(
            companyId,
            startDate,
            endDate,
          );

        dispatch({
          type: this.retrieveContactsGatheredAction,
          contactsGathered: response,
        });
        return response;
      } catch (error) {
        commonHandler(dispatch)(error);
      }
    };
  }

  clearEmailAnalytics() {
    return async (dispatch: Dispatch<StoreState>) => {
      await dispatch({
        type: this.clearEmailAnalyticsAction,
      });
    };
  }

  resetAllBotAnalytics() {
    return async (dispatch: Dispatch<StoreState>) => {
      await dispatch({
        type: this.resetAllAction,
      });
    };
  }
}

const botAnalyticsCRUDActions = new BotAnalyticsCRUDActions();

export default botAnalyticsCRUDActions;

export const useBotAnalyticsCRUDActions = getActionHookBuilder(
  botAnalyticsCRUDActions,
  [
    'retrieveTopDialogues',
    'retrieveContactsAmountAnalytics',
    'retrieveUnansweredQuestions',
    'exportUnansweredQuestions',
    'retrieveContactsByType',
    'exportQAKeywords',
    'retrieveQAKeywords',
    'retrieveEmailAnalytics',
    'retrieveXRayAnalytics',
    'retrieveContactsSourcesAnalytics',
    'retrieveDemographicAnalytics',
    'retrieveContactsGathered',
    'clearEmailAnalytics',
    'resetAllBotAnalytics',
  ],
);
